<script setup lang="ts">
import BackComponent from '@/components/BackComponent.vue';
import BottomBarComponent from '@/components/BottomBarComponent.vue';
import NextComponent from '@/components/NextComponent.vue';
import { formatCurrency } from '@/helpers';
import { useAgreementStore } from '@/stores/agreement';
import local from '@/stores/local';
import { useMessageStore } from '@/stores/messageStore';
import type { Addon, AgreementType, CalculateDto, CalculateResponse, SamAgreement } from '@/types';
import axios from 'axios';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

const message = useMessageStore()

const { agreement, agreementType, selectedAddons, calculation, samAgreements } = storeToRefs(useAgreementStore())
const loading = ref(true)
const addons = computed<Addon[]>(() => {
  const result: Addon[] = []

  calculation.value?.additional?.forEach(addon => {
    if (agreementType.value?.includeAddons?.includes(addon.name)) {
      return
    }
    else if (agreementType.value?.incompatibleAddons?.includes(addon.name)) {
      return
    }
    else {
      result.push(addon)
    }
  })
      return result.sort((a, b) => a.name.localeCompare(b.name));
})

async function calculatePrice(): Promise<CalculateResponse | undefined> {
  const { brandId, modelId, engineId, registrationYear, duration, yearlyMileage, oldMileage } = agreement.value
  const { customBrandId: customBrandId, customModelId: customModelId, customEngineId: customEngineId } = agreementType.value as AgreementType

  if (brandId == null || modelId == null || engineId == null) throw 'Brand, model and engine IDs must be set!'
  if (duration == null) throw 'Duration must be set'
  if (yearlyMileage == null) throw 'yearlyMileage must be set'

  const calculateDto: CalculateDto = {
    brandId,
    modelId,
    engineId,
    duration,
    yearlyMileage
  };

  if (customBrandId != null && customModelId != null && customEngineId != null) {
    calculateDto.brandId = customBrandId
    calculateDto.modelId = customModelId
    calculateDto.engineId = customEngineId
  }

  if (registrationYear !== new Date().getFullYear()) {
    if (registrationYear == null && oldMileage == null) throw 'Tried to set registrationYear and oldMileage, but something is missing'

    calculateDto.registrationYear = registrationYear
    calculateDto.oldMileage = oldMileage
  }

  try {
    const { data } = await axios.post('proxy/calculate', calculateDto)

    calculation.value = data
    local.setItem('calculation', JSON.parse(JSON.stringify(data)))
    return data
  } catch (error) {
    console.error(error)
    message.addError('Der skete en fejl ved beregning af pris. Prøv at gå et skridt tilbage og frem igen. Hvis problemet fortsætter kan du kontakte os direkte.')
  }
}

function onSelectAddon(addon: Addon) {
  if (selectedAddons.value.map(x => x.id).includes(addon.id)) {
    selectedAddons.value = selectedAddons.value.filter(x => x.id !== addon.id)
  } else {
    selectedAddons.value.push(addon)
  }
}

async function getSamAgreements() {
  const { data } = await axios.get('proxy/agreements')
  samAgreements.value = data
  local.setItem<SamAgreement[]>('samAgreements', data)
}

onBeforeRouteLeave(() => {
  local.setItem('selectedAddons', JSON.parse(JSON.stringify(selectedAddons.value)))
})

onMounted(async () => {
  loading.value = true

  try {
    await calculatePrice()
    await getSamAgreements()
  } catch (error) {
    console.error(error)
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <v-container>
    <div class="d-flex flex-column" style="gap: 1rem;">
      <v-fade-transition group mode="in-out" hide-on-leave>
        <div v-if="loading">
          <v-skeleton-loader type="heading@5" />
        </div>
        <v-card
          v-else-if="addons.length"
          v-for="addon in addons"
          :key="addon.id"
          :color="selectedAddons.map(x => x.id).includes(addon.id) ? 'success' : 'primary'"
          @click="onSelectAddon(addon)"
        >
          <v-card-text>
            <div class="d-flex align-start" style="gap: .5rem;">
              <div class="flex-grow-1">
                {{ addon.name }}
              </div>
              <div>
                <strong> {{ formatCurrency(addon.price) }} </strong>
              </div>
            </div>
          </v-card-text>
        </v-card>
        <v-empty-state
          v-else
          title="Ingen tilgængelige tillægsydelser"
          text="Der blev ikke fundet tillægsydelser du kan tilføje til aftalen"
        />
      </v-fade-transition>
    </div>
  </v-container>

  <BottomBarComponent>
    <BackComponent to="/agreement/payment" />
    <NextComponent to="/agreement/customer" />
  </BottomBarComponent>
</template>